import _extends from "@babel/runtime/helpers/extends";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _kebabCase from "lodash/kebabCase";
import _snakeCase from "lodash/snakeCase";
var _excluded = ["ref"];
import "core-js/modules/es6.array.map.js";
import React, { useState, useEffect, useRef } from 'react';
import { ControlledTabs, useTabState, Box } from '@nerdwallet/currency';
import { useImpression, ImpressionContext } from '@nerdwallet/analytics/react/impression';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Tracking } from '@nerdwallet/nw-client-lib';
import classNames from 'classnames';
import { CompareGridTabContent } from '../compare-grid-tab-content/compare-grid-tab-content';
import { SearchEntityToCompare } from '../search-entity-to-compare/search-entity-to-compare';
import styles from '../comparison-grid.module.less';
import { EVENT_TYPES, ENTITY_NAME, ACTION, INTERACTION_TYPE } from '../../lib/analytics-constants';
export var CompareGridTabs = function CompareGridTabs(_ref) {
  var gridTabs = _ref.gridTabs,
      addEntityClickHandler = _ref.addEntityClickHandler,
      hideComparisonGridPanel = _ref.hideComparisonGridPanel,
      removeEntityClickHandler = _ref.removeEntityClickHandler,
      productType = _ref.productType,
      taxonomy = _ref.taxonomy,
      renderStickyBanner = _ref.renderStickyBanner,
      classes = _ref.classes;

  /**
   * We want to trigger analytics when tab state is updated, but we don't want to fire up on first render
   * that's why we use this flag to prevent firing on initial render
   */
  var initialRender = useRef(true);
  /**
   * @type {[Boolean, Function]} show search component
   */

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      shouldShowSearch = _useState2[0],
      updateShouldShowSearch = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      showNumber = _useState4[0],
      updateShowNumber = _useState4[1];

  var getTabComponent = function getTabComponent(tab, tabPosition) {
    var tabName = renderStickyBanner ? "pl_prequal_offer_compare_".concat(_snakeCase(tab.label), "_tab") : "".concat(_snakeCase(tab.label), "_tab"); // eslint-disable-next-line

    var _useImpression = useImpression({
      entityName: ENTITY_NAME.COMPARISON_GRID_TAB,
      eventType: EVENT_TYPES.ELEMENT_IMPRESSION,
      payload: {
        element_position: tabPosition,
        section_name: tabName,
        section_position: tabPosition
      }
    }),
        ref = _useImpression.ref,
        impression = _objectWithoutProperties(_useImpression, _excluded);

    var selectedEntityCntClass = classNames([styles.tabLabelEntityCircle, !showNumber && styles.tabLabelEntityCircleDisabled]);

    var getLabel = function getLabel(_ref2) {
      var label = _ref2.label,
          entitiesToCompare = _ref2.entitiesToCompare,
          showSelectedEntitiesCount = _ref2.showSelectedEntitiesCount;
      return /*#__PURE__*/React.createElement("div", {
        className: classNames([styles.controlledTabs, styles.tab, styles[_kebabCase(tab === null || tab === void 0 ? void 0 : tab.label)], classes === null || classes === void 0 ? void 0 : classes.tabLabel])
      }, label, showSelectedEntitiesCount && (entitiesToCompare === null || entitiesToCompare === void 0 ? void 0 : entitiesToCompare.length) > 0 && /*#__PURE__*/React.createElement("span", {
        className: classNames(selectedEntityCntClass, classes === null || classes === void 0 ? void 0 : classes.tabLabelCounter)
      }, entitiesToCompare.length));
    };

    var compareGridTabContentProps = _objectSpread(_objectSpread({}, tab), {}, {
      entitiesToCompare: tab.entitiesToCompare || [],
      removeEntityClickHandler: removeEntityClickHandler,
      addEntityClickHandler: addEntityClickHandler,
      updateShouldShowSearch: updateShouldShowSearch,
      productType: productType,
      taxonomy: taxonomy,
      tabName: tabName,
      tabPosition: tabPosition,
      hideComparisonGridPanel: hideComparisonGridPanel,
      renderStickyBanner: renderStickyBanner
    });

    var getTabContent = function getTabContent() {
      return /*#__PURE__*/React.createElement("div", {
        key: tab.label,
        ref: ref,
        className: classes === null || classes === void 0 ? void 0 : classes.tabContent
      }, /*#__PURE__*/React.createElement(ElementImpression, {
        id: "".concat(ENTITY_NAME.COMPARISON_GRID_TAB, "_").concat(tabName),
        elementName: ENTITY_NAME.COMPARISON_GRID_TAB,
        elementPosition: tabPosition,
        sectionCategory: tabName,
        sectionPosition: tabPosition
      }, /*#__PURE__*/React.createElement(ImpressionContext.Provider, {
        value: impression
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        className: classNames(classes === null || classes === void 0 ? void 0 : classes.tabContentDesktop, shouldShowSearch ? styles.compareGridTabContentWrapperHide : styles.compareGridTabContentWrapperDesktop)
      }, /*#__PURE__*/React.createElement(CompareGridTabContent, compareGridTabContentProps)), /*#__PURE__*/React.createElement("div", {
        className: classNames(classes === null || classes === void 0 ? void 0 : classes.tabContentMobile, shouldShowSearch ? styles.compareGridTabContentWrapperHide : styles.compareGridTabContentWrapperMobile)
      }, /*#__PURE__*/React.createElement(CompareGridTabContent, _extends({
        isMobile: true
      }, compareGridTabContentProps))), /*#__PURE__*/React.createElement("div", {
        className: shouldShowSearch && tab.canAdd ? styles.searchContainer : styles.searchContainerHide
      }, /*#__PURE__*/React.createElement(SearchEntityToCompare, {
        addEntityClickHandler: addEntityClickHandler,
        productType: productType,
        entitiesToCompare: tab.entitiesToCompare,
        updateShouldShowSearch: updateShouldShowSearch,
        tabName: tabName
      }))))));
    };

    return {
      label: getLabel(tab),
      content: getTabContent(),
      trackingProps: {
        action: ACTION.SELECT,
        entityName: ENTITY_NAME.COMPARISON_GRID_TAB,
        interactionType: INTERACTION_TYPE.PRESS
      }
    };
  };

  var tabComponent = Array.isArray(gridTabs.tabs) && gridTabs.tabs.length ? gridTabs.tabs.map(function (tab, index) {
    return getTabComponent(tab, index);
  }) : [];
  var tabConfig = {
    tabs: tabComponent,
    baseId: 'compare-grid-tabs',
    defaultTabIndex: gridTabs.activeTabIndex
  }; // @ts-ignore // TODO: remove when Tab accepts react.node as label

  var tabState = useTabState(tabConfig);
  useEffect(function () {
    if (!initialRender.current) {
      updateShouldShowSearch(false);
      updateShowNumber(!showNumber);
      Tracking.formInputChanged({
        formName: ENTITY_NAME.COMPARISON_GRID_TAB,
        fieldName: 'CLICK'
      });
    } else {
      // change flag after first render
      initialRender.current = false;
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [tabState.selectedIndex]);
  var emptyState = typeof gridTabs.emptyState === 'function' ? gridTabs.emptyState(hideComparisonGridPanel) : gridTabs.emptyState; // We cannot allow a user to pass an empty tabs array. @nerdwallet/currency require to
  // has at least one tab if we had it before.
  // Instead of that we can check whole tabs and if there are no tabs with either
  // entitiesToCompare or canAdd feature we can show an empty state

  var hasEntitiesToReview = gridTabs.tabs.some(function (_ref3) {
    var entitiesToCompare = _ref3.entitiesToCompare,
        canAdd = _ref3.canAdd;
    return Array.isArray(entitiesToCompare) && entitiesToCompare.length || canAdd;
  });
  return /*#__PURE__*/React.createElement(Box, {
    className: classNames(styles.tabWrapper, classes === null || classes === void 0 ? void 0 : classes.tabWrapper)
  }, !hasEntitiesToReview && emptyState ? emptyState : /*#__PURE__*/React.createElement(ControlledTabs, {
    hasSpacingBetweenTabsAndContent: false,
    tabState: tabState,
    tracking: {
      enabled: true,
      sectionCategory: ENTITY_NAME.COMPARISON_GRID_TAB
    }
  }));
};